import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import customerOrderService from '../services/customerOrderService';
import productService from '../services/productService';
import qboService from '../services/qboService';
import xeroService from '../services/xeroService';
import documentService from '../services/documentService';
import documentCategoryService from '../services/documentCategoryService';
import SearchableSelect from '../components/SearchableSelect';
import ConfirmModal from '../components/ConfirmModal';

const DOC_ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

const API_URL = process.env.REACT_APP_API_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CHANNEL_OPTIONS = [
  { value: '', label: 'No default' },
  { value: 'retail', label: 'Retail' },
  { value: 'food_service', label: 'Food Service' },
];

const LANG_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
];

// ── Product search typeahead ──────────────────────────────────────────────────
function ProductSearch({ products, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = products.find(p => p.id === value);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q)
    ).slice(0, 30);
  }, [products, query]);

  return (
    <div className="position-relative">
      <input
        className="form-control form-control-sm"
        placeholder="Search product..."
        value={open ? query : (selected ? selected.name : '')}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={e => setQuery(e.target.value)}
      />
      {open && filtered.length > 0 && (
        <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1060, maxHeight: 180, overflowY: 'auto' }}>
          {filtered.map(p => (
            <li key={p.id} className="list-group-item list-group-item-action py-1 small"
              onMouseDown={() => { onChange(p); setOpen(false); }}>
              {p.name}{p.sku ? <span className="text-muted ms-2">({p.sku})</span> : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Math-evaluating price input ───────────────────────────────────────────────
function MathPriceInput({ value, onChange, className, placeholder, min, step }) {
  const [raw, setRaw] = useState(value != null ? String(value) : '');

  // Keep raw in sync when parent resets value (e.g. after save)
  useEffect(() => {
    setRaw(value != null ? String(value) : '');
  }, [value]);

  const handleBlur = () => {
    const trimmed = raw.trim();
    if (!trimmed) { onChange(''); return; }
    // Only allow digits, operators, parens, dots, spaces
    const safe = trimmed.replace(/[^0-9+\-*/().\s]/g, '');
    if (!safe) { onChange(''); return; }
    try {
      // eslint-disable-next-line no-new-func
      const result = new Function('return ' + safe)();
      if (typeof result === 'number' && isFinite(result) && result >= 0) {
        const rounded = Math.round(result * 10000) / 10000;
        setRaw(String(rounded));
        onChange(String(rounded));
      } else {
        onChange(raw);
      }
    } catch {
      onChange(raw);
    }
  };

  return (
    <input
      type="text"
      className={className}
      placeholder={placeholder}
      min={min}
      step={step}
      value={raw}
      onChange={e => { setRaw(e.target.value); onChange(e.target.value); }}
      onBlur={handleBlur}
    />
  );
}

// ── Price List Modal ──────────────────────────────────────────────────────────
function PriceListModal({ customer, products, show, onClose }) {
  const [priceList, setPriceList] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newItem, setNewItem] = useState({ productId: null, presentationLabel: '', unitsPerCase: '', pricePerCase: '', qbItemCode: '', mirrorPriceListItemId: '' });
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState('');
  const [qboItems, setQboItems] = useState([]); // available QBO item names for dropdown

  // Reseller module (docs/RESELLER_MODULE.md): if this tenant is a reseller, its resale catalog is
  // locked to items the parent company has priced for it — no free product/label entry.
  const [parentCatalog, setParentCatalog] = useState([]);
  const [isResellerTenant, setIsResellerTenant] = useState(false);

  const load = useCallback(async () => {
    if (!customer) return;
    setLoading(true);
    setErr('');
    try {
      const pl = await customerOrderService.getPriceListByCustomer(customer.id);
      setPriceList(pl);
      setItems(pl?.Items || []);
    } catch {
      setPriceList(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if (show) {
      load();
      setNewItem({ productId: null, presentationLabel: '', unitsPerCase: '', pricePerCase: '', qbItemCode: '', mirrorPriceListItemId: '' });
      setEditingId(null);
      qboService.getItems().then(setQboItems).catch(() => setQboItems([]));
      customerOrderService.getResellerParentCatalog()
        .then(catalog => { setParentCatalog(catalog); setIsResellerTenant(true); })
        .catch(() => { setParentCatalog([]); setIsResellerTenant(false); });
    }
  }, [show, load]);

  const ensurePriceList = async () => {
    if (priceList) return priceList;
    const pl = await customerOrderService.createPriceList({
      customerId: customer.id,
      label: `${customer.name} Price List`,
    });
    setPriceList(pl);
    return pl;
  };

  const handleAddItem = async () => {
    if (isResellerTenant) {
      if (!newItem.mirrorPriceListItemId) {
        return setErr('Select a wholesale item — resale items must come from the parent company\'s catalog');
      }
    } else if (!newItem.productId && !newItem.presentationLabel) {
      return setErr('Select a product or enter a label');
    }
    setAdding(true);
    setErr('');
    try {
      const pl = await ensurePriceList();
      const product = products.find(p => p.id === newItem.productId);
      await customerOrderService.addPriceListItem(pl.id, isResellerTenant ? {
        mirrorPriceListItemId: newItem.mirrorPriceListItemId,
        presentationLabel: newItem.presentationLabel.trim() || null,
        pricePerCase: newItem.pricePerCase ? Number(newItem.pricePerCase) : null,
        qbItemCode: newItem.qbItemCode.trim() || null,
      } : {
        productId: newItem.productId || null,
        presentationLabel: newItem.presentationLabel || product?.name || '',
        unitsPerCase: newItem.unitsPerCase ? Number(newItem.unitsPerCase) : null,
        pricePerCase: newItem.pricePerCase ? Number(newItem.pricePerCase) : null,
        qbItemCode: newItem.qbItemCode.trim() || null,
      });
      setNewItem({ productId: null, presentationLabel: '', unitsPerCase: '', pricePerCase: '', qbItemCode: '', mirrorPriceListItemId: '' });
      load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to add item');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      presentationLabel: item.presentationLabel,
      unitsPerCase: item.unitsPerCase ?? '',
      pricePerCase: item.pricePerCase ?? '',
      qbItemCode: item.qbItemCode ?? '',
      active: item.active,
      mirrorPriceListItemId: item.mirrorPriceListItemId ?? '',
    });
  };

  const handleSaveEdit = async (itemId) => {
    setSaving(s => ({ ...s, [itemId]: true }));
    try {
      await customerOrderService.updatePriceListItem(itemId, isResellerTenant ? {
        mirrorPriceListItemId: editForm.mirrorPriceListItemId,
        presentationLabel: editForm.presentationLabel.trim() || null,
        pricePerCase: editForm.pricePerCase !== '' ? Number(editForm.pricePerCase) : null,
        qbItemCode: editForm.qbItemCode.trim() || null,
        active: editForm.active,
      } : {
        presentationLabel: editForm.presentationLabel,
        unitsPerCase: editForm.unitsPerCase !== '' ? Number(editForm.unitsPerCase) : null,
        pricePerCase: editForm.pricePerCase !== '' ? Number(editForm.pricePerCase) : null,
        qbItemCode: editForm.qbItemCode.trim() || null,
        active: editForm.active,
      });
      setEditingId(null);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(s => ({ ...s, [itemId]: false }));
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Remove this item from the price list?')) return;
    setDeleting(d => ({ ...d, [itemId]: true }));
    try {
      await customerOrderService.deletePriceListItem(itemId);
      load();
    } catch {
      setErr('Failed to delete');
    } finally {
      setDeleting(d => ({ ...d, [itemId]: false }));
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          Price List — <span className="text-primary">{customer?.name}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger py-2 small">{err}<button className="btn-close btn-sm float-end" onClick={() => setErr('')} /></div>}

        {loading ? (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        ) : (
          <>
            {/* Existing items */}
            {items.length > 0 ? (
              <div className="table-responsive mb-3">
                <table className="table table-sm table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Product</th>
                      <th>Packaging Type</th>
                      <th className="text-end" style={{ width: 70 }}>U/cs</th>
                      <th className="text-end" style={{ width: 110 }}>Price/cs ($)</th>
                      <th style={{ width: 120 }}>QB Item Code</th>
                      <th className="text-center" style={{ width: 70 }}>Active</th>
                      <th style={{ width: 110 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id} className={!item.active ? 'table-secondary text-muted' : ''}>
                        <td className="small">{item.Product?.name || <span className="text-muted">—</span>}</td>
                        <td>
                          {editingId === item.id ? (
                            <input className="form-control form-control-sm" value={editForm.presentationLabel}
                              onChange={e => setEditForm(f => ({ ...f, presentationLabel: e.target.value }))}
                              placeholder={isResellerTenant ? 'What your customers see' : ''} />
                          ) : (
                            <span>{item.presentationLabel}</span>
                          )}
                        </td>
                        <td className="text-end">
                          {editingId === item.id && !isResellerTenant ? (
                            <input type="number" className="form-control form-control-sm text-end" value={editForm.unitsPerCase}
                              onChange={e => setEditForm(f => ({ ...f, unitsPerCase: e.target.value }))} min="1" />
                          ) : (
                            item.unitsPerCase ?? <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-end">
                          {editingId === item.id ? (
                            <MathPriceInput
                              className="form-control form-control-sm text-end"
                              value={editForm.pricePerCase}
                              onChange={v => setEditForm(f => ({ ...f, pricePerCase: v }))}
                              placeholder="0.00" min="0" step="0.01"
                            />
                          ) : (
                            item.pricePerCase != null
                              ? <strong>${Number(item.pricePerCase).toFixed(2)}</strong>
                              : <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {editingId === item.id ? (
                            <SearchableSelect
                              options={[{ value: '', label: '— not linked —' }, ...qboItems.map(qi => ({ value: qi.name, label: qi.name }))]}
                              value={editForm.qbItemCode || ''}
                              onChange={val => setEditForm(f => ({ ...f, qbItemCode: val }))}
                              placeholder="— not linked —"
                              size="sm"
                            />
                          ) : (
                            item.qbItemCode
                              ? <code className="small">{item.qbItemCode}</code>
                              : <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td className="text-center">
                          {editingId === item.id ? (
                            <input type="checkbox" className="form-check-input" checked={editForm.active}
                              onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))} />
                          ) : (
                            <span className={`badge ${item.active ? 'bg-success' : 'bg-secondary'}`}>
                              {item.active ? 'Yes' : 'No'}
                            </span>
                          )}
                        </td>
                        <td>
                          {editingId === item.id ? (
                            <div className="d-flex gap-1">
                              <button className="btn btn-success btn-sm" onClick={() => handleSaveEdit(item.id)} disabled={saving[item.id]}>
                                {saving[item.id] ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
                              </button>
                              <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                            </div>
                          ) : (
                            <div className="d-flex gap-1">
                              <button className="btn btn-outline-secondary btn-sm px-2" onClick={() => startEdit(item)} title="Edit">✏️</button>
                              <button className="btn btn-outline-danger btn-sm px-2" onClick={() => handleDelete(item.id)}
                                disabled={deleting[item.id]} title="Remove">🗑</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted fst-italic mb-3">No items yet. Add the first SKU below.</p>
            )}

            {/* Add new item row */}
            <div className="border rounded p-3 bg-light">
              <h6 className="small fw-semibold text-muted mb-2">Add Packaging Type</h6>
              {isResellerTenant ? (
                <div className="row g-2 mb-2 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label small mb-1">
                      Wholesale Item <span className="text-danger">*</span>{' '}
                      <small className="text-muted">(locked to the parent company's catalog)</small>
                    </label>
                    <SearchableSelect
                      options={parentCatalog.map(pi => ({
                        value: pi.id,
                        label: `${pi.presentationLabel}${pi.pricePerCase != null ? ` — $${Number(pi.pricePerCase).toFixed(2)}/cs wholesale` : ''}`,
                      }))}
                      value={newItem.mirrorPriceListItemId}
                      onChange={val => {
                        const picked = parentCatalog.find(pi => pi.id === val);
                        setNewItem(n => ({
                          ...n,
                          mirrorPriceListItemId: val,
                          presentationLabel: n.presentationLabel || picked?.presentationLabel || '',
                        }));
                      }}
                      placeholder="Select wholesale item..."
                      size="sm"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">
                      Display Name <small className="text-muted">(what your customers see)</small>
                    </label>
                    <input className="form-control form-control-sm" placeholder="e.g. Neapolitan Cheese Pizza 12&quot;"
                      value={newItem.presentationLabel}
                      onChange={e => setNewItem(n => ({ ...n, presentationLabel: e.target.value }))} />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small mb-1">Your Price / cs ($)</label>
                    <MathPriceInput
                      className="form-control form-control-sm"
                      placeholder="e.g. 10*8"
                      value={newItem.pricePerCase}
                      onChange={v => setNewItem(n => ({ ...n, pricePerCase: v }))}
                      min="0" step="0.01"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small mb-1">QB Item <small className="text-muted">(opt.)</small></label>
                    <SearchableSelect
                      options={[{ value: '', label: '— not linked —' }, ...qboItems.map(qi => ({ value: qi.name, label: qi.name }))]}
                      value={newItem.qbItemCode || ''}
                      onChange={val => setNewItem(n => ({ ...n, qbItemCode: val }))}
                      placeholder="— not linked —"
                      size="sm"
                    />
                  </div>
                  <div className="col-md-1">
                    <button className="btn btn-primary btn-sm w-100" onClick={handleAddItem} disabled={adding}>
                      {adding ? <span className="spinner-border spinner-border-sm" /> : '+ Add'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="row g-2 mb-2 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Product</label>
                      <ProductSearch
                        products={products}
                        value={newItem.productId}
                        onChange={p => setNewItem(n => ({
                          ...n,
                          productId: p.id,
                          presentationLabel: n.presentationLabel || p.name,
                        }))}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Packaging Type <span className="text-danger">*</span></label>
                      <input className="form-control form-control-sm" placeholder='e.g. "Retail 8pk"'
                        value={newItem.presentationLabel}
                        onChange={e => setNewItem(n => ({ ...n, presentationLabel: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-1">Units / cs</label>
                      <input type="number" className="form-control form-control-sm" placeholder="e.g. 8"
                        value={newItem.unitsPerCase} min="1"
                        onChange={e => setNewItem(n => ({ ...n, unitsPerCase: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-1">Price / cs ($)</label>
                      <MathPriceInput
                        className="form-control form-control-sm"
                        placeholder="e.g. 10*8"
                        value={newItem.pricePerCase}
                        onChange={v => setNewItem(n => ({ ...n, pricePerCase: v }))}
                        min="0" step="0.01"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-1">QB Item <small className="text-muted">(opt.)</small></label>
                      <SearchableSelect
                        options={[{ value: '', label: '— not linked —' }, ...qboItems.map(qi => ({ value: qi.name, label: qi.name }))]}
                        value={newItem.qbItemCode || ''}
                        onChange={val => setNewItem(n => ({ ...n, qbItemCode: val }))}
                        placeholder="— not linked —"
                        size="sm"
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button className="btn btn-primary btn-sm px-4" onClick={handleAddItem} disabled={adding}>
                      {adding ? <span className="spinner-border spinner-border-sm" /> : '+ Add Item'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Portal Link Modal ─────────────────────────────────────────────────────────
function PortalModal({ customer, show, onClose, onTokenRegenerated }) {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');

  const portalUrl = customer?.portalToken
    ? `${window.location.origin}/order/${customer.portalToken}`
    : null;

  const handleCopy = () => {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRegenerate = async () => {
    setConfirmRegenerate(false);
    setRegenerating(true);
    setRegenerateError('');
    try {
      await axios.post(`${API_URL}/api/customers/${customer.id}/regenerate-token`, {}, auth());
      onTokenRegenerated();
      onClose();
    } catch {
      setRegenerateError('Failed to regenerate token.');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Portal Link — {customer?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {regenerateError && <div className="alert alert-danger py-2 small">{regenerateError}</div>}
        {portalUrl ? (
          <>
            <p className="text-muted small mb-2">
              Share this URL with the customer. They can submit orders without creating an account.
            </p>
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control form-control-sm"
                value={portalUrl}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
              />
              <button className={`btn btn-sm ${copied ? 'btn-success' : 'btn-outline-secondary'}`} onClick={handleCopy}>
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <div className="d-flex gap-2">
              <a href={portalUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                Open portal ↗
              </a>
            </div>
            <hr />
            <div>
              <p className="text-muted small mb-2">
                <strong>Regenerate link</strong> — creates a new token and invalidates the current URL.
                Only do this if the link was shared with the wrong person.
              </p>
              <button className="btn btn-outline-danger btn-sm" onClick={() => setConfirmRegenerate(true)} disabled={regenerating}>
                {regenerating ? <span className="spinner-border spinner-border-sm" /> : 'Regenerate token'}
              </button>
            </div>
          </>
        ) : (
          <p className="text-muted">No portal token assigned. Save the customer to generate one.</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
      <ConfirmModal
        show={confirmRegenerate}
        title="Regenerate portal link?"
        message="This will invalidate the existing portal link. The customer will need the new URL. Continue?"
        confirmLabel="Regenerate"
        onConfirm={handleRegenerate}
        onCancel={() => setConfirmRegenerate(false)}
      />
    </Modal>
  );
}

// ── Customer Documents / Certificates Modal ────────────────────────────────────
function CustomerDocumentsModal({ customer, show, onClose }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    if (!customer?.id) return;
    setLoading(true);
    documentService.getDocumentsForCustomer(customer.id)
      .then(setDocuments)
      .catch(() => setDocuments([]))
      .finally(() => setLoading(false));
  }, [customer?.id]);

  useEffect(() => {
    if (!show) return;
    setError('');
    load();
    documentCategoryService.getCategories()
      .then(cats => {
        const active = (cats || []).filter(c => c.isActive);
        setCategories(active);
        const defaultCat = active.find(c => c.systemKey === 'customer_w9') || active[0];
        setCategoryId(prev => prev || (defaultCat ? String(defaultCat.id) : ''));
      })
      .catch(() => setCategories([]));
  }, [show, load]);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!DOC_ALLOWED_MIME.includes(f.type)) {
      setError('Only PDF, JPEG, and PNG files are allowed.');
      return;
    }
    setError('');
    setUploadFile(f);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile || !categoryId) return;
    setUploading(true);
    setUploadProgress(0);
    setError('');
    try {
      const { uploadUrl, publicUrl } = await documentService.getUploadUrl(uploadFile.name, uploadFile.type, uploadFile.size);
      await axios.put(uploadUrl, uploadFile, {
        headers: { 'Content-Type': uploadFile.type },
        onUploadProgress: evt => setUploadProgress(Math.round((evt.loaded / evt.total) * 100)),
      });
      const category = categories.find(c => String(c.id) === String(categoryId));
      await documentService.createDocument({
        name: category?.name || uploadFile.name,
        documentCategoryId: categoryId,
        fileUrl: publicUrl,
        fileName: uploadFile.name,
        fileSize: uploadFile.size,
        mimeType: uploadFile.type,
        expiryDate: expiryDate || null,
        customerId: customer.id,
      });
      setUploadFile(null);
      setExpiryDate('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await documentService.deleteDocument(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch {
      setError('Failed to delete document.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Documents — {customer?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        {loading ? (
          <div className="text-center py-3"><span className="spinner-border spinner-border-sm" /></div>
        ) : documents.length === 0 ? (
          <p className="text-muted small mb-3">No documents uploaded yet.</p>
        ) : (
          <ul className="list-group mb-3">
            {documents.map(doc => (
              <li key={doc.id} className="list-group-item d-flex align-items-center justify-content-between py-2">
                <div>
                  <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="fw-semibold" style={{ fontSize: 13 }}>
                    {doc.fileName}
                  </a>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {doc.category?.name || 'Uncategorized'}
                    {doc.expiryDate && ` · Expires ${doc.expiryDate}`}
                  </div>
                </div>
                <button className="btn btn-outline-danger btn-sm" onClick={() => setDeleteTarget(doc)}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}

        <hr />
        <form onSubmit={handleUploadSubmit}>
          <div className="mb-2">
            <label className="form-label small fw-semibold">Type</label>
            <select
              className="form-select form-select-sm"
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              required
            >
              <option value="">— Select —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label small fw-semibold">File (PDF, JPEG, or PNG)</label>
            <input
              ref={fileInputRef}
              type="file"
              className="form-control form-control-sm"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label small fw-semibold">Expiration Date (optional)</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={expiryDate}
              onChange={e => setExpiryDate(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={uploading || !uploadFile || !categoryId}>
            {uploading ? `Uploading… ${uploadProgress}%` : 'Upload'}
          </button>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
      <ConfirmModal
        show={!!deleteTarget}
        title="Delete document?"
        message={deleteTarget ? `Delete "${deleteTarget.fileName}"? This cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Modal>
  );
}

// ── Customer Edit/Add Modal ───────────────────────────────────────────────────
function CustomerModal({ customer, show, onClose, onSaved }) {
  const empty = {
    name: '', email: '', address: '', phone: '', defaultOrderChannel: '', portalLanguage: 'en', creditApplicationRequired: false,
    addressLine1: '', addressLine2: '', city: '', stateProvince: '', postalCode: '', country: '',
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const [scanning, setScanning] = useState(false);
  const [scanNote, setScanNote] = useState('');
  const w9InputRef = useRef(null);

  const [qboConnected, setQboConnected] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushErr, setPushErr] = useState('');
  const [showPushConfirm, setShowPushConfirm] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(customer
        ? { name: customer.name, email: customer.email || '', address: customer.address || '', phone: customer.phone || '',
            defaultOrderChannel: customer.defaultOrderChannel || '', portalLanguage: customer.portalLanguage || 'en',
            creditApplicationRequired: !!customer.creditApplicationRequired,
            addressLine1: customer.addressLine1 || '', addressLine2: customer.addressLine2 || '',
            city: customer.city || '', stateProvince: customer.stateProvince || '',
            postalCode: customer.postalCode || '', country: customer.country || '' }
        : empty
      );
      setErr('');
      setScanNote('');
      setPushErr('');
      qboService.getStatus().then(s => setQboConnected(!!s.connected)).catch(() => setQboConnected(false));
    }
  }, [show, customer]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!form.name.trim()) return setErr('Name is required');
    setSaving(true);
    setErr('');
    try {
      if (customer) {
        await axios.put(`${API_URL}/api/customers/${customer.id}`, form, auth());
      } else {
        await axios.post(`${API_URL}/api/customers`, form, auth());
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleW9Scan = async (file) => {
    if (!file) return;
    setScanning(true);
    setErr('');
    setScanNote('');
    try {
      const { data: uploadData } = await axios.post(
        `${API_URL}/api/customers/w9-scan/upload-url`,
        { filename: file.name, contentType: file.type },
        auth()
      );
      await fetch(uploadData.uploadUrl, { method: 'PUT', body: file });
      const { data: extracted } = await axios.post(
        `${API_URL}/api/customers/w9-scan`,
        { documentKey: uploadData.key, contentType: file.type },
        auth()
      );
      setForm(f => ({
        ...f,
        name: extracted.companyName || f.name,
        addressLine1: extracted.addressLine1 || f.addressLine1,
        addressLine2: extracted.addressLine2 || f.addressLine2,
        city: extracted.city || f.city,
        stateProvince: extracted.stateProvince || f.stateProvince,
        postalCode: extracted.postalCode || f.postalCode,
        country: extracted.country || f.country,
      }));
      setScanNote('Extracted from W9 — review and fill in the rest (email, phone, channel).');
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to scan W9');
    } finally {
      setScanning(false);
    }
  };

  const handlePush = async () => {
    setShowPushConfirm(false);
    setPushing(true);
    setPushErr('');
    try {
      await qboService.pushCustomerToQbo(customer.id);
      onSaved();
      onClose();
    } catch (e) {
      setPushErr(e.response?.data?.message || 'Failed to push to QuickBooks');
    } finally {
      setPushing(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">{customer ? 'Edit Customer' : 'New Customer'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger py-2 small">{err}</div>}
        {scanNote && <div className="alert alert-info py-2 small">{scanNote}</div>}
        {!customer && (
          <div className="mb-3">
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={scanning}
              onClick={() => w9InputRef.current?.click()}>
              {scanning ? <><span className="spinner-border spinner-border-sm me-2" />Scanning…</> : '📄 Scan W9'}
            </button>
            <input ref={w9InputRef} type="file" accept="image/*,application/pdf" hidden
              onChange={e => { const f = e.target.files?.[0]; if (f) handleW9Scan(f); e.target.value = ''; }} />
            <div className="form-text" style={{ fontSize: 11 }}>
              Upload a W9 to prefill the company name and address below.
            </div>
          </div>
        )}
        <div className="row g-3 mb-3">
          <div className="col-md-7">
            <label className="form-label small fw-semibold">Name <span className="text-danger">*</span></label>
            <input className="form-control" value={form.name} autoFocus
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="col-md-5">
            <label className="form-label small fw-semibold">Phone</label>
            <input className="form-control" value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          </div>
        </div>
        <div className="row g-3 mb-3">
          <div className="col-md-7">
            <label className="form-label small fw-semibold">Email</label>
            <input type="email" className="form-control" value={form.email} placeholder="orders@customer.com"
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="col-md-5">
            <label className="form-label small fw-semibold">Address</label>
            <input className="form-control" value={form.address}
              onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
          </div>
        </div>

        <div className="mb-1 small fw-semibold text-muted">Billing Address (for QuickBooks)</div>
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <label className="form-label form-label-sm">Street address</label>
            <input className="form-control form-control-sm" placeholder="Line 1" value={form.addressLine1}
              onChange={e => setForm(f => ({ ...f, addressLine1: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label form-label-sm" style={{ visibility: 'hidden' }}>Line 2</label>
            <input className="form-control form-control-sm" placeholder="Line 2 (optional)" value={form.addressLine2}
              onChange={e => setForm(f => ({ ...f, addressLine2: e.target.value }))} />
          </div>
          <div className="col-md-5">
            <label className="form-label form-label-sm">City</label>
            <input className="form-control form-control-sm" value={form.city}
              onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-sm">State</label>
            <input className="form-control form-control-sm" value={form.stateProvince}
              onChange={e => setForm(f => ({ ...f, stateProvince: e.target.value }))} />
          </div>
          <div className="col-md-2">
            <label className="form-label form-label-sm">ZIP code</label>
            <input className="form-control form-control-sm" value={form.postalCode}
              onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))} />
          </div>
          <div className="col-md-2">
            <label className="form-label form-label-sm">Country</label>
            <input className="form-control form-control-sm" value={form.country}
              onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Default Order Channel</label>
            <select className="form-select" value={form.defaultOrderChannel}
              onChange={e => setForm(f => ({ ...f, defaultOrderChannel: e.target.value }))}>
              {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Portal Language</label>
            <select className="form-select" value={form.portalLanguage}
              onChange={e => setForm(f => ({ ...f, portalLanguage: e.target.value }))}>
              {LANG_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-check mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="creditAppRequired"
            checked={form.creditApplicationRequired}
            onChange={e => setForm(f => ({ ...f, creditApplicationRequired: e.target.checked }))}
          />
          <label className="form-check-label small" htmlFor="creditAppRequired">
            Require credit application before first portal order
          </label>
          <div className="form-text" style={{ fontSize: 11 }}>
            The customer will be shown the credit application form the next time they open their
            portal link, and can't place orders until they submit it.
          </div>
        </div>
        {pushErr && <div className="alert alert-danger py-2 small mt-3 mb-0">{pushErr}</div>}
      </Modal.Body>
      <Modal.Footer>
        {customer && !customer.qbCustomerId && qboConnected && (
          <Button variant="outline-success" className="me-auto" disabled={pushing}
            onClick={() => setShowPushConfirm(true)}>
            {pushing ? <><span className="spinner-border spinner-border-sm me-2" />Pushing…</> : 'Push to QuickBooks'}
          </Button>
        )}
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : customer ? 'Save Changes' : 'Add Customer'}
        </Button>
      </Modal.Footer>
      <ConfirmModal
        show={showPushConfirm}
        title="Push to QuickBooks?"
        message={`This creates "${customer?.name}" as a QuickBooks customer (display name set to the company name) using the billing address above. Make sure the street address, city, state, and ZIP are filled in.`}
        confirmLabel="Push"
        variant="primary"
        onConfirm={handlePush}
        onCancel={() => setShowPushConfirm(false)}
      />
    </Modal>
  );
}

// ── Credit Application View Modal ──────────────────────────────────────────────
function CreditApplicationViewModal({ customer, show, onClose }) {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  const [revealOpen, setRevealOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState('');
  const [accountNumber, setAccountNumber] = useState(null);

  useEffect(() => {
    if (!show || !customer?.id) return;
    setLoading(true);
    setApp(null);
    setNotFound(false);
    setError('');
    setAccountNumber(null);
    setRevealOpen(false);
    setPassword('');
    setRevealError('');
    axios.get(`${API_URL}/api/customers/${customer.id}/credit-app`, auth())
      .then(r => setApp(r.data.creditApplication))
      .catch(e => {
        if (e.response?.status === 404) setNotFound(true);
        else setError('Failed to load credit application.');
      })
      .finally(() => setLoading(false));
  }, [show, customer?.id]);

  const handleReveal = async () => {
    if (!password) return;
    setRevealing(true);
    setRevealError('');
    try {
      const r = await axios.post(`${API_URL}/api/customers/${customer.id}/credit-app/reveal`, { password }, auth());
      setAccountNumber(r.data.accountNumber);
      setRevealOpen(false);
      setPassword('');
    } catch (e) {
      setRevealError(e.response?.data?.message || 'Failed to reveal account number.');
    } finally {
      setRevealing(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Credit Application — {customer?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="text-center py-3"><span className="spinner-border spinner-border-sm" /></div>}
        {notFound && <p className="text-muted mb-0">No credit application submitted yet.</p>}
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        {app && (
          <>
            <div className="row g-2 mb-2" style={{ fontSize: 13 }}>
              <div className="col-6"><strong>Full Name:</strong> {app.fullName || '—'}</div>
              <div className="col-6"><strong>Title:</strong> {app.title || '—'}</div>
              <div className="col-6"><strong>Company Name:</strong> {app.companyName || '—'}</div>
              <div className="col-6"><strong>Business Type:</strong> {app.businessType || '—'}</div>
              <div className="col-6"><strong>In Business Since:</strong> {app.inBusinessSince || '—'}</div>
              <div className="col-6"><strong>Phone:</strong> {app.phone || '—'}</div>
              <div className="col-6"><strong>Email:</strong> {app.email || '—'}</div>
              <div className="col-6"><strong>Fax:</strong> {app.fax || '—'}</div>
              <div className="col-12"><strong>Registered Address:</strong> {app.registeredAddress || '—'}, {app.cityStateZip || '—'}</div>
              <div className="col-6"><strong>How Long at Address:</strong> {app.howLongAtAddress || '—'}</div>
              <div className="col-6"><strong>A/P Responsible:</strong> {app.apResponsibleName || '—'} {app.apResponsibleTitle ? `(${app.apResponsibleTitle})` : ''}</div>
              <div className="col-6"><strong>A/P Responsible Email:</strong> {app.apResponsibleEmail || '—'}</div>
            </div>
            <hr />
            <div className="row g-2 mb-2" style={{ fontSize: 13 }}>
              <div className="col-6"><strong>Bank Name:</strong> {app.bankName || '—'}</div>
              <div className="col-6"><strong>Bank Phone:</strong> {app.bankPhone || '—'}</div>
              <div className="col-12"><strong>Bank Address:</strong> {app.bankAddress || '—'}</div>
              <div className="col-6"><strong>Account Type:</strong> {app.accountType || '—'}</div>
              <div className="col-6">
                <strong>Account Number:</strong>{' '}
                {accountNumber
                  ? <span className="font-monospace">{accountNumber}</span>
                  : app.hasAccountNumber
                    ? <button className="btn btn-sm btn-outline-secondary" onClick={() => setRevealOpen(true)}>🔒 Reveal</button>
                    : '—'}
              </div>
            </div>
            {revealOpen && (
              <div className="p-2 mb-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                {revealError && <div className="alert alert-danger py-1 small mb-2">{revealError}</div>}
                <label className="form-label small fw-semibold">Confirm your password to reveal</label>
                <div className="input-group input-group-sm">
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleReveal()}
                    autoFocus
                  />
                  <button className="btn btn-primary" onClick={handleReveal} disabled={revealing || !password}>
                    {revealing ? '…' : 'Reveal'}
                  </button>
                </div>
              </div>
            )}
            <hr />
            <div className="mb-2">
              <strong style={{ fontSize: 13 }}>Trade References</strong>
              {(app.References || []).length === 0 ? (
                <p className="text-muted small mb-0 mt-1">None listed.</p>
              ) : (
                <table className="table table-sm mt-2" style={{ fontSize: 12 }}>
                  <thead>
                    <tr><th>Company</th><th>Contact</th><th>Phone</th><th>Email</th></tr>
                  </thead>
                  <tbody>
                    {app.References.map(r => (
                      <tr key={r.id}>
                        <td>{r.companyName || '—'}</td>
                        <td>{r.referenceName || '—'}</td>
                        <td>{r.phone || '—'}</td>
                        <td>{r.email || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <hr />
            <div className="mb-2">
              <strong style={{ fontSize: 13 }}>Signature</strong>
              {app.signatureSignedUrl && (
                <div className="mt-2 p-2 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6', display: 'inline-block' }}>
                  <img src={app.signatureSignedUrl} alt="Signature" style={{ maxWidth: 260, maxHeight: 90, display: 'block' }} />
                </div>
              )}
              <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                Signed by {app.signedByName || '—'} on {app.signedAt ? new Date(app.signedAt).toLocaleString() : '—'}
                {app.signedIp && ` from ${app.signedIp}`}.
              </div>
            </div>
            {app.pdfSignedUrl && (
              <a
                href={app.pdfSignedUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-primary btn-sm mt-1"
              >
                📄 Download Signed Application (PDF)
              </a>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminCustomerPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [flash, setFlash] = useState(null); // { msg, type }
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState(''); // debounced value sent to API

  const [editModal, setEditModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);

  const [priceListModal, setPriceListModal] = useState(false);
  const [priceListCustomer, setPriceListCustomer] = useState(null);

  const [portalModal, setPortalModal] = useState(false);
  const [portalCustomer, setPortalCustomer] = useState(null);
  const [accountingProvider, setAccountingProvider] = useState(null); // 'qbo' | 'xero' | null

  const [documentsModal, setDocumentsModal] = useState(false);
  const [documentsCustomer, setDocumentsCustomer] = useState(null);

  const [creditAppModal, setCreditAppModal] = useState(false);
  const [creditAppCustomer, setCreditAppCustomer] = useState(null);

  const fetchCustomers = useCallback(async (page = 1, q = '') => {
    try {
      const params = { page, limit: 30 };
      if (q) params.search = q;
      const res = await axios.get(`${API_URL}/api/customers`, { ...auth(), params });
      const data = res.data;
      setCustomers(data.customers || data);
      setTotalPages(data.totalPages || 1);
    } catch {
      showFlash('Error fetching customers', 'danger');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search input → reset to page 1 when query changes
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { fetchCustomers(currentPage, search); }, [currentPage, search, fetchCustomers]);
  useEffect(() => { productService.getProducts({ excludeIntermediate: true }).then(setProducts).catch(() => {}); }, []);
  useEffect(() => {
    xeroService.getStatus().then(s => { if (s.connected) setAccountingProvider('xero'); }).catch(() => {});
  }, []);

  const showFlash = (msg, type = 'success') => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 3000);
  };

  const openEdit = (customer = null) => { setEditCustomer(customer); setEditModal(true); };
  const openPriceList = (customer) => { setPriceListCustomer(customer); setPriceListModal(true); };
  const openPortal = (customer) => { setPortalCustomer(customer); setPortalModal(true); };
  const openDocuments = (customer) => { setDocumentsCustomer(customer); setDocumentsModal(true); };
  const openCreditApp = (customer) => { setCreditAppCustomer(customer); setCreditAppModal(true); };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete ${customer.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/customers/${customer.id}`, auth());
      showFlash('Customer deleted');
      fetchCustomers(currentPage, search);
    } catch {
      showFlash('Error deleting customer', 'danger');
    }
  };

  const CHANNEL_BADGE = { retail: 'bg-info', food_service: 'bg-warning text-dark' };
  const CHANNEL_LABEL = { retail: 'Retail', food_service: 'Food Service' };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h4 className="mb-0">Customers</h4>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <div className="position-relative">
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Search customers…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ width: 220, paddingLeft: '2rem' }}
            />
            <span className="position-absolute text-muted" style={{ left: '0.5rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '0.8rem' }}>
              🔍
            </span>
          </div>
          {accountingProvider === 'xero' ? (
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/admin/xero/sync?tab=contacts')}>
              Sync with Xero
            </button>
          ) : (
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/admin/qbo/sync?tab=customers')}>
              Sync with QBO
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => openEdit()}>+ Add Customer</button>
        </div>
      </div>

      {flash && (
        <div className={`alert alert-${flash.type} alert-dismissible py-2`}>
          {flash.msg}
          <button className="btn-close btn-sm" onClick={() => setFlash(null)} />
        </div>
      )}

      {customers.length === 0 ? (
        <div className="text-center text-muted py-5">
          {search ? (
            <p className="mb-0">No customers match <strong>"{search}"</strong>.</p>
          ) : (
            <>
              <p className="mb-2">No customers yet.</p>
              <button className="btn btn-primary btn-sm" onClick={() => openEdit()}>Add your first customer</button>
            </>
          )}
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">Name</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Channel</th>
                  <th>Portal</th>
                  <th className="text-end pe-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id}>
                    <td className="ps-3 fw-semibold">{c.name}</td>
                    <td className="text-muted small">{c.address || '—'}</td>
                    <td className="text-muted small">{c.phone || '—'}</td>
                    <td>
                      {c.defaultOrderChannel
                        ? <span className={`badge small ${CHANNEL_BADGE[c.defaultOrderChannel]}`}>
                            {CHANNEL_LABEL[c.defaultOrderChannel]}
                          </span>
                        : <span className="text-muted small">—</span>}
                    </td>
                    <td>
                      {c.portalToken
                        ? <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => openPortal(c)}>
                            🔗 Portal link
                          </button>
                        : <span className="text-muted small">no token</span>}
                    </td>
                    <td className="text-end pe-3">
                      <div className="d-flex gap-1 justify-content-end">
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => openEdit(c)}>Edit</button>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => openPriceList(c)}>Price List</button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => openDocuments(c)}>📄 Documents</button>
                        {c.creditApplicationRequired && (
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => openCreditApp(c)}>💳 Credit App</button>
                        )}
                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(c)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
              <button className="btn btn-outline-secondary btn-sm" disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}>&laquo; Previous</button>
              <span className="small text-muted">Page {currentPage} of {totalPages}</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}>Next &raquo;</button>
            </div>
          )}
        </div>
      )}

      <CustomerModal
        show={editModal}
        customer={editCustomer}
        onClose={() => setEditModal(false)}
        onSaved={() => { showFlash(editCustomer ? 'Customer updated' : 'Customer added'); fetchCustomers(currentPage, search); }}
      />

      <PriceListModal
        show={priceListModal}
        customer={priceListCustomer}
        products={products}
        onClose={() => setPriceListModal(false)}
      />

      <PortalModal
        show={portalModal}
        customer={portalCustomer}
        onClose={() => setPortalModal(false)}
        onTokenRegenerated={() => fetchCustomers(currentPage, search)}
      />
      <CustomerDocumentsModal
        show={documentsModal}
        customer={documentsCustomer}
        onClose={() => setDocumentsModal(false)}
      />
      <CreditApplicationViewModal
        show={creditAppModal}
        customer={creditAppCustomer}
        onClose={() => setCreditAppModal(false)}
      />
    </div>
  );
}
